import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAfterWrite } from '@/api/refresh';
import { baseApi as api } from '.';
import type { LogoVariant } from './baseApi';
import { baseQueryKeys } from './queryKeys';
import type {
  BackupSettings,
  ChangePasswordInput,
  LocationPatch,
  NewLocationInput,
  NewRoleInput,
  NewSeasonInput,
  NewSportInput,
  NewUserInput,
  Organization,
  RedeemCodeInput,
  ReportSettings,
  RolePatch,
  SeasonPatch,
  SetupInput,
  SportListEntryInput,
  SportListEntryPatch,
  SportListKind,
  SportPatch,
  UserUpdateInput,
} from './types';

/** A change to the sports refreshes them (and what is drawn from them: athlete positions, team pickers). */
function useAfterSportChange() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: baseQueryKeys.sports.root });
}
export function useCreateSport() {
  const after = useAfterSportChange();
  return useMutation({ mutationFn: (input: NewSportInput) => api.sports.create(input), onSuccess: after });
}
export function useUpdateSport() {
  const after = useAfterSportChange();
  return useMutation({ mutationFn: (v: { id: string; patch: SportPatch }) => api.sports.update(v.id, v.patch), onSuccess: after });
}
/** Deletes a sport nothing refers to (a sport made by mistake); one with history is retired instead. */
export function useDeleteSport() {
  const after = useAfterWrite();
  return useMutation({ mutationFn: (id: string) => api.sports.remove(id), onSuccess: () => after() });
}

/* ------------------------------------------------- what a sport keeps: lists */

/** A change to a list refreshes it, and the sports (whose `positions` are drawn from it), and what forms offer from it. */
function useAfterListChange() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: baseQueryKeys.sportLists.root }), qc.invalidateQueries({ queryKey: baseQueryKeys.sports.root })]);
}
export function useCreateSportListEntry(kind: SportListKind) {
  const after = useAfterListChange();
  return useMutation({ mutationFn: (input: SportListEntryInput) => api.sportLists.create(kind, input), onSuccess: after });
}
export function useUpdateSportListEntry(kind: SportListKind) {
  const after = useAfterListChange();
  return useMutation({ mutationFn: (v: { id: string; patch: SportListEntryPatch }) => api.sportLists.update(kind, v.id, v.patch), onSuccess: after });
}
export function useDeleteSportListEntry(kind: SportListKind) {
  const after = useAfterListChange();
  return useMutation({ mutationFn: (id: string) => api.sportLists.remove(kind, id), onSuccess: after });
}
export function useReorderSportList(kind: SportListKind) {
  const after = useAfterListChange();
  return useMutation({ mutationFn: (v: { ids: string[]; sportId?: string | null }) => api.sportLists.reorder(kind, v.ids, v.sportId), onSuccess: after });
}

/* ---------------------------------------------------------------- seasons */

/** A season's name and dates are drawn on team lists, athlete pages and reports, and a sport names its current season, so any change refreshes what shows them as well. */
function useAfterSeasonChange() {
  const after = useAfterWrite();
  return () => after();
}
export function useCreateSeason() {
  const after = useAfterSeasonChange();
  return useMutation({ mutationFn: (input: NewSeasonInput) => api.seasons.create(input), onSuccess: after });
}
export function useUpdateSeason() {
  const after = useAfterSeasonChange();
  return useMutation({ mutationFn: (v: { id: string; patch: SeasonPatch }) => api.seasons.update(v.id, v.patch), onSuccess: after });
}
export function useRemoveSeason() {
  const after = useAfterSeasonChange();
  return useMutation({ mutationFn: (id: string) => api.seasons.remove(id), onSuccess: after });
}

/* -------------------------------------------------------------- locations */

function useAfterLocationChange() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: baseQueryKeys.locations });
}
export function useCreateLocation() {
  const after = useAfterLocationChange();
  return useMutation({ mutationFn: (input: NewLocationInput) => api.locations.create(input), onSuccess: after });
}
export function useUpdateLocation() {
  const after = useAfterLocationChange();
  return useMutation({ mutationFn: (v: { id: string; patch: LocationPatch }) => api.locations.update(v.id, v.patch), onSuccess: after });
}

/* -------------------------------------------------------- report settings */

export function useUpdateReportSettings() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (patch: Partial<ReportSettings>) => api.reportSettings.update(patch), onSuccess: () => qc.invalidateQueries({ queryKey: baseQueryKeys.reportSettings }) });
}

/* ------------------------------------------------------------------ apps */

/** Installing or uninstalling changes what every screen may show, so everything is fetched again (the menu comes from the bootstrap). */
function useAfterAppChange() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries();
}
export function useInstallApp() {
  const after = useAfterAppChange();
  return useMutation({ mutationFn: (id: string) => api.apps.install(id), onSuccess: after });
}
export function useUninstallApp() {
  const after = useAfterAppChange();
  return useMutation({ mutationFn: (id: string) => api.apps.uninstall(id), onSuccess: after });
}

/* -------------------------------------------------------------- backups */

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Organization>) => api.organization.update(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseQueryKeys.bootstrap }),
  });
}
/** The logo is part of the organization the whole interface wears, so the session's copy of it is fetched again. */
export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { variant: LogoVariant; file: Blob }) => api.organization.uploadLogo(v.variant, v.file), onSuccess: () => qc.invalidateQueries({ queryKey: baseQueryKeys.bootstrap }) });
}
export function useRemoveLogo() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (variant: LogoVariant) => api.organization.removeLogo(variant), onSuccess: () => qc.invalidateQueries({ queryKey: baseQueryKeys.bootstrap }) });
}

export function useCreateBackup() {
  const after = useAfterWrite();
  return useMutation({ mutationFn: () => api.backups.create(), onSuccess: after });
}
export function useUpdateBackupSettings() {
  const after = useAfterWrite();
  return useMutation({ mutationFn: (patch: Partial<BackupSettings>) => api.backups.updateSettings(patch), onSuccess: after });
}

/* ------------------------------------------------ base: sign-in and accounts */

/** A new person's session replaces whatever was cached, so nothing fetched for someone else can show. */
export function useSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api.auth.signIn(v.email, v.password),
    onSuccess: () => qc.clear(),
  });
}
export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.auth.signOut(), onSettled: () => qc.clear() });
}
export const useForgotPassword = () => useMutation({ mutationFn: (email: string) => api.auth.forgotPassword(email) });
export const useResetPassword = () => useMutation({ mutationFn: (input: RedeemCodeInput) => api.auth.resetPassword(input) });
export const useAcceptInvitation = () => useMutation({ mutationFn: (input: RedeemCodeInput) => api.auth.acceptInvitation(input) });
export const useChangePassword = () => useMutation({ mutationFn: (input: ChangePasswordInput) => api.auth.changePassword(input) });
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { firstName: string; lastName: string }) => api.auth.updateProfile(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseQueryKeys.bootstrap }),
  });
}
export function useRunSetup() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: SetupInput) => api.setup.run(input), onSuccess: () => qc.clear() });
}

function useAfterUserChange() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: baseQueryKeys.users });
}
export function useInviteUser() {
  const after = useAfterUserChange();
  return useMutation({ mutationFn: (input: NewUserInput) => api.users.invite(input), onSuccess: after });
}
export function useUpdateUser() {
  const qc = useQueryClient();
  const after = useAfterUserChange();
  // The administrator may have changed their own access, which the menus and screens are drawn from.
  return useMutation({ mutationFn: (v: { id: string; input: UserUpdateInput }) => api.users.update(v.id, v.input), onSuccess: () => Promise.all([after(), qc.invalidateQueries({ queryKey: baseQueryKeys.bootstrap })]) });
}
export function useSetUserActive() {
  const after = useAfterUserChange();
  return useMutation({ mutationFn: (v: { id: string; active: boolean }) => api.users.setActive(v.id, v.active), onSuccess: after });
}
export function useIssueUserCode() {
  const after = useAfterUserChange();
  return useMutation({ mutationFn: (id: string) => api.users.issueCode(id), onSuccess: after });
}

/**
 * A role change refreshes the role list, and the people (which roles a person's access amounts to is worked out from the roles as they stand), and
 * the session's own label. A role is only a starting point, so no one's permissions change with it.
 */
function useAfterRoleChange(affectsLabels: boolean) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: baseQueryKeys.roles });
    if (affectsLabels) {
      qc.invalidateQueries({ queryKey: baseQueryKeys.users });
      qc.invalidateQueries({ queryKey: baseQueryKeys.bootstrap });
    }
  };
}
export function useCreateRole() {
  const after = useAfterRoleChange(false); // a brand-new role has no access yet, so it matches no one
  return useMutation({ mutationFn: (input: NewRoleInput) => api.roles.create(input), onSuccess: after });
}
export function useUpdateRole() {
  const after = useAfterRoleChange(true);
  return useMutation({ mutationFn: (v: { key: string; patch: RolePatch }) => api.roles.update(v.key, v.patch), onSuccess: after });
}
export function useDeleteRole() {
  const after = useAfterRoleChange(true);
  return useMutation({ mutationFn: (key: string) => api.roles.delete(key), onSuccess: after });
}

/** Restoring replaces everything, so whatever was fetched before is not to be trusted (the service also ends every session). */
export function useRestoreBackup() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { path: string; confirm: string }) => api.backups.restore(v.path, v.confirm), onSuccess: () => qc.clear() });
}

/** Reads a backup file and says what is in it; changes nothing. */
export const useInspectBackup = () => useMutation({ mutationFn: (path: string) => api.backups.inspectPackage(path) });
